// src/components/dentally/DentallyLeadSyncSection.jsx
//
// "Details from Dentally" — shown under the Dentally patient card in Personal
// details when the route practice has an active Dentally integration AND this
// lead is linked to a Dentally patient.
//
// It answers one question: where does the CRM record differ from the linked
// Dentally patient, and lets a member of staff copy the values they trust. The
// per-field wording matches the review wizard so the same difference reads the
// same way wherever staff meet it.
//
// Constraints this component is built around:
//   - It must never block or slow the lead page. The check starts when this
//     section mounts, shows one compact line while it runs, and a failure is a
//     quiet inline line with "Try again" — never a red page-level alert.
//   - `can_apply` from the server is the SOLE authority for enabling the apply
//     control: it already folds in permission, link state, whether the
//     integration is active, and whether the lead is archived. The frontend
//     integration scope decides only whether this section renders at all, so
//     the two can never disagree about the button.
//   - When the server blocks applying it says why (`blocked_reason`), and the
//     differences are shown read-only with that one-line explanation.
//   - After a successful apply the lead is refetched into Redux by the caller so
//     the Personal details fields above update without a page reload.
import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box, Button, Checkbox, FormControlLabel, Stack, Typography,
} from '@mui/material';
import Iconify from '../Iconify';
import useDentallyLeadSync from '../../hooks/useDentallyLeadSync';
import { sectionCardStyle, sectionLabelStyle } from '../../styles/DentallyBookingStyle';
import {
  describeApplyFailure,
  describeBlockedReason,
  describeDifference,
  describeSkipped,
  differenceLabel,
  formatCheckedAt,
  joinLabels,
  dentallyValueAriaLabel,
} from '../../utils/dentallyLeadSyncPresentation';

/** "Update 2 details" / "Update 1 detail" / "Update details" while nothing is ticked. */
const applyButtonLabel = (count, applying) => {
  if (applying) return 'Updating…';
  if (count === 0) return 'Update details';
  return count === 1 ? 'Update 1 detail' : `Update ${count} details`;
};

const quietLineSx = { display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' };

export default function DentallyLeadSyncSection({ practiceId, leadId, onApplied, sync: providedSync }) {
  // The Personal details panel now reads /dentally/sync/ once and shares it with
  // the patient card, which renders `patient_summary` and the last-compared time
  // from the same response. When it passes that result down, this section must
  // not fetch it a second time — so the local hook is disabled (null ids) rather
  // than called conditionally, which would change hook order.
  const ownSync = useDentallyLeadSync(providedSync ? null : practiceId, providedSync ? null : leadId);
  const sync = providedSync || ownSync;
  const {
    differences, isLoading, loaded, linked, providerError, isApplying, isArchived, applyOutcome, blockedReason,
    pushState,
  } = sync;
  // CONFLICT means a push found the Dentally record changed too, so it wrote
  // nothing. It belongs here rather than in a dialog of its own: this is
  // already where staff compare the two records field by field, and the
  // differences listed below are exactly the choice they need to make.
  const hasConflict = pushState?.state === 'CONFLICT';
  const [ticked, setTicked] = useState([]);

  const availableFields = useMemo(() => differences.map((diff) => diff.field), [differences]);
  // Drop ticks for fields that no longer differ (after an apply or a re-check).
  useEffect(() => {
    setTicked((prev) => prev.filter((field) => availableFields.includes(field)));
  }, [availableFields]);

  // `isArchived` is the 409 backstop for a lead archived between load and apply;
  // the server normally reports that up front through can_apply/blocked_reason.
  const canEdit = sync.canApply && !isArchived;
  const toggle = (field) => setTicked((prev) => (
    prev.includes(field) ? prev.filter((f) => f !== field) : [...prev, field]
  ));

  const handleApply = async () => {
    const result = await sync.apply(ticked);
    // The applied field list goes to the caller so it can refresh exactly those
    // inputs; a bare refetch leaves the form showing pre-apply values.
    if (result.ok && result.appliedFields.length > 0) onApplied?.(result.appliedFields);
  };

  // One inline sentence saying what actually happened. Every failure path the
  // contract defines writes nothing, so each is reported as such rather than
  // leaving the operator guessing whether a partial write landed.
  const receipt = (() => {
    if (!applyOutcome) return null;
    if (applyOutcome.archived || applyOutcome.failed) {
      return { tone: 'text.secondary', text: describeApplyFailure(applyOutcome.failureCode) };
    }
    if (applyOutcome.appliedFields.length === 0) return { tone: 'text.secondary', text: 'Nothing was changed.' };
    const names = joinLabels(applyOutcome.appliedFields.map((field) => differenceLabel({ field }).toLowerCase()));
    return { tone: 'success.dark', text: `Updated ${names}.` };
  })();

  // Nothing to say: still checking is handled below, but an unlinked lead or a
  // finished check with no link renders nothing at all.
  if (loaded && !linked && !providerError && !hasConflict) return null;

  return (
    <Box sx={{ ...sectionCardStyle, mb: 3 }} data-testid="dentally-lead-sync-section">
      <Typography sx={sectionLabelStyle}>DETAILS FROM DENTALLY</Typography>

      {hasConflict && (
        <Box sx={{ ...quietLineSx, mb: 1.5 }} data-testid="dentally-lead-sync-conflict">
          <Iconify icon="eva:alert-triangle-outline" width={16} height={16} sx={{ color: 'warning.dark' }} />
          <Typography variant="body2" color="warning.dark">
            These details were changed in Dentally too, so your edit wasn’t sent.
            Choose which values to keep below.
          </Typography>
        </Box>
      )}

      {isLoading && (
        <Typography variant="body2" color="text.secondary" data-testid="dentally-lead-sync-loading">
          Checking Dentally for changes…
        </Typography>
      )}

      {!isLoading && providerError && (
        <Box sx={quietLineSx} data-testid="dentally-lead-sync-provider-error">
          <Typography variant="body2" color="text.secondary">Couldn’t reach Dentally just now.</Typography>
          <Button size="small" variant="text" onClick={sync.refresh}>Try again</Button>
        </Box>
      )}

      {!isLoading && !providerError && !hasConflict && differences.length === 0 && (
        <Box sx={quietLineSx} data-testid="dentally-lead-sync-match">
          <Typography variant="body2" color="text.secondary">
            CRM and Dentally match.
            {formatCheckedAt(sync.checkedAt) ? ` ${formatCheckedAt(sync.checkedAt)}.` : ''}
          </Typography>
          <Button size="small" variant="text" onClick={sync.refresh}>Check again</Button>
        </Box>
      )}

      {!isLoading && !providerError && differences.length > 0 && (
        <Box data-testid="dentally-lead-sync-differences">
          <Typography variant="body2" sx={{ mb: 0.5 }}>
            {differences.length === 1
              ? 'One detail is different in Dentally.'
              : `${differences.length} details are different in Dentally.`}
          </Typography>

          {differences.map((diff) => (
            <Stack
              key={diff.field}
              direction="row"
              alignItems="flex-start"
              spacing={1}
              flexWrap="wrap"
              useFlexGap
              sx={{ py: 0.25 }}
              data-testid={`dentally-lead-sync-difference-${diff.field}`}
            >
              <Typography variant="body2" sx={{ flex: '1 1 280px', minWidth: 0, pt: 0.5, wordBreak: 'break-word' }}>
                {describeDifference(diff)}
              </Typography>
              {canEdit && (
                <FormControlLabel
                  sx={{ m: 0, '& .MuiFormControlLabel-label': { fontSize: 14 } }}
                  control={(
                    <Checkbox
                      size="small"
                      checked={ticked.includes(diff.field)}
                      onChange={() => toggle(diff.field)}
                      sx={{ p: 0.5 }}
                      inputProps={{ 'aria-label': dentallyValueAriaLabel(diff) }}
                    />
                  )}
                  label="Use the Dentally value"
                />
              )}
            </Stack>
          ))}

          {canEdit && (
            <Box sx={{ mt: 1.5 }}>
              <Button
                size="small"
                variant="contained"
                disabled={ticked.length === 0 || isApplying}
                onClick={handleApply}
                startIcon={<Iconify icon="mdi:content-copy" />}
                data-testid="dentally-lead-sync-apply"
              >
                {applyButtonLabel(ticked.length, isApplying)}
              </Button>
              {ticked.length === 0 && (
                <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75 }}>
                  Tick the details you want to copy from Dentally.
                </Typography>
              )}
            </Box>
          )}

          {!canEdit && (
            <Typography
              variant="caption"
              color="text.secondary"
              component="p"
              sx={{ mt: 1.5 }}
              data-testid="dentally-lead-sync-blocked"
              data-reason={isArchived ? 'lead_archived' : (blockedReason || 'permission')}
            >
              {describeBlockedReason(isArchived ? 'lead_archived' : blockedReason)}
            </Typography>
          )}
        </Box>
      )}

      {receipt && (
        <Typography
          variant="body2"
          sx={{ mt: 1.5, color: receipt.tone }}
          data-testid="dentally-lead-sync-receipt"
        >
          {receipt.text}
        </Typography>
      )}

      {applyOutcome?.skipped?.length > 0 && (
        <Box sx={{ mt: 0.5 }} data-testid="dentally-lead-sync-skipped">
          {applyOutcome.skipped.map((entry) => (
            <Typography key={entry.field} variant="caption" color="text.secondary" component="p">
              {describeSkipped(entry)}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
}

DentallyLeadSyncSection.propTypes = {
  practiceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  leadId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  onApplied: PropTypes.func,
  /** useDentallyLeadSync result shared by the caller; omit to fetch locally. */
  sync: PropTypes.object,
};

DentallyLeadSyncSection.defaultProps = {
  practiceId: null,
  leadId: null,
  onApplied: undefined,
  sync: null,
};
